import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import {
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { BuildingsModel } from '../../../../../types';
import { ResourceTemplateFormValues } from '../resource-templates-form-page';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { SectionHeader } from '../../../../shared/section-header';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';

interface StepBasicInformationProps {
  control: Control<ResourceTemplateFormValues>;
  errors: FieldErrors<ResourceTemplateFormValues>;
  watch: UseFormWatch<ResourceTemplateFormValues>;
  setValue: UseFormSetValue<ResourceTemplateFormValues>;
  buildingsList: BuildingsModel[];
  isLoading: boolean;
  isFetching: boolean;
}

export default function StepBasicInformation({
  control,
  errors,
  watch,
  setValue,
  buildingsList,
  isLoading,
  isFetching,
}: StepBasicInformationProps) {
  const { calendarRoles } = useUserSettings();

  const buildingId = watch('buildingId');
  const building = watch('building');

  const resourceCategoryOptions = [
    { displayValue: 'Undefined', value: 'Resource Category' },
    ...(isAbleToSeeUIElement(
      calendarRoles,
      CalendarRoles.CanSelectCategoryUnknown.value
    )
      ? [{ displayValue: 'Unknown', value: 'CATEGORY_UNKNOWN' }]
      : []),
    ...(isAbleToSeeUIElement(
      calendarRoles,
      CalendarRoles.CanSelectConferenceRoom.value
    )
      ? [{ displayValue: 'Conference Room', value: 'CONFERENCE_ROOM' }]
      : []),
    ...(isAbleToSeeUIElement(calendarRoles, CalendarRoles.CanSelectOther.value)
      ? [{ displayValue: 'Other', value: 'OTHER' }]
      : []),
  ];

  return (
    <Box sx={{ viewTransitionName: 'steps', width: '100%' }}>
      <Stack gap={2} sx={{ mb: 4 }}>
        <SectionHeader>General Information</SectionHeader>
        <Grid container columnSpacing={2} rowSpacing={1.5} size={{ xs: 10 }}>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  required
                  autoComplete="off"
                  error={!!errors.name}
                  helperText={errors?.name?.message || ' '}
                  label="Template Name"
                  sx={{ width: '100%' }}
                  value={field.value || ''}
                  onChange={field.onChange}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Controller
              name="desc"
              control={control}
              render={({ field }) => (
                <TextField
                  required
                  error={!!errors.desc}
                  helperText={errors?.desc?.message || ' '}
                  label="Template Description"
                  sx={{ width: '100%' }}
                  value={field.value || ''}
                  onChange={field.onChange}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Controller
              name="resourceCategory"
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  label="Resource Category"
                  sx={{ width: '100%' }}
                  value={field.value || ''}
                  onChange={field.onChange}
                >
                  {resourceCategoryOptions?.map((option) => (
                    <MenuItem value={option.value} key={option.value}>
                      {option.displayValue}
                    </MenuItem>
                  )) || []}
                </TextField>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Controller
              name="resourceType"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Resource Type"
                  sx={{ width: '100%' }}
                  value={field.value || ''}
                  onChange={field.onChange}
                />
              )}
            />
          </Grid>
        </Grid>
      </Stack>
      <Stack gap={2} sx={{ mb: 4 }}>
        <SectionHeader>Building Information</SectionHeader>
        <Grid container columnSpacing={2} rowSpacing={1.5} size={{ xs: 10 }}>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Controller
              name="buildingId"
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  disabled={isLoading && isFetching}
                  InputProps={{
                    endAdornment:
                      isLoading && isFetching ? (
                        <CircularProgress
                          color="inherit"
                          size={20}
                          sx={{ mr: 2 }}
                        />
                      ) : null,
                  }}
                  label="Building"
                  sx={{ width: '100%' }}
                  value={field.value || ''}
                  helperText={' '}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    setValue('floorName', '');
                    const selected =
                      buildingsList.find(
                        (b) => b.buildingId === e.target.value
                      ) || null;
                    setValue('building', selected);
                  }}
                >
                  {buildingsList.map((b) => (
                    <MenuItem value={b.buildingId} key={b.buildingId}>
                      {b.buildingName}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Controller
              name="floorName"
              control={control}
              render={({ field }) => (
                <TextField
                  disabled={(isLoading && isFetching) || !buildingId}
                  select
                  label="Floor"
                  sx={{ width: '100%' }}
                  value={field.value || ''}
                  helperText={' '}
                  onChange={field.onChange}
                >
                  {building?.floorNames?.map((floor) => (
                    <MenuItem value={floor} key={floor}>
                      {floor}
                    </MenuItem>
                  )) || []}
                </TextField>
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <Controller
              name="floorSection"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Floor Section"
                  sx={{ width: '100%' }}
                  value={field.value || ''}
                  onChange={field.onChange}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Controller
              name="capacity"
              control={control}
              render={({ field }) => (
                <TextField
                  inputProps={{ min: 1 }}
                  label="Capacity"
                  type="number"
                  sx={{ width: '100%' }}
                  value={field.value || 1}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value ? Number(e.target.value) : e.target.value
                    )
                  }
                />
              )}
            />
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
}
